import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const StoryScrambleGame = ({ gameData, onComplete }) => {
    const [userSequence, setUserSequence] = useState([]);
    const [availableParts, setAvailableParts] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [showHints, setShowHints] = useState(false);

    useEffect(() => {
        if (gameData?.story_parts) {
            const shuffled = [...gameData.story_parts].sort(() => Math.random() - 0.5);
            setAvailableParts(shuffled);
            setUserSequence([]);
            setFeedback(null);
        }
    }, [gameData]);

    const addPart = (part) => {
        if (feedback) return;
        triggerHaptic(25);
        setUserSequence(prev => [...prev, part]);
        setAvailableParts(prev => prev.filter(p => p.id !== part.id));
    };

    const removePart = (part) => {
        if (feedback) return;
        triggerHaptic(20);
        setUserSequence(prev => prev.filter(p => p.id !== part.id));
        setAvailableParts(prev => [...prev, part]);
    };

    const clearSequence = () => {
        if (feedback) return;
        triggerHaptic(40);
        setAvailableParts(prev => [...prev, ...userSequence]);
        setUserSequence([]);
    };

    const checkAnswer = () => {
        if (feedback || userSequence.length === 0) return;
        const isCorrect = userSequence.every((part, index) => part.order === index + 1);

        if (isCorrect) {
            setFeedback('correct');
            const points = (gameData.points_per_part || 25) * gameData.story_parts.length;
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                onComplete?.(points);
            }, 4000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
            }, 3000);
        }
    };

    if (!gameData || !gameData.story_parts) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-purple-100 animate-pulse bg-white">
                <div className="text-9xl mb-8">📚</div>
                <h3 className="text-base md:text-3xl font-black text-purple-900 tracking-tighter uppercase">Maišomi pasakos lapai...</h3>
            </div>
        );
    }

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-purple-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-20 md:h-20 bg-purple-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        📚
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            {gameData.story_title || 'Pasakos Scramble'}
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <button
                        onClick={() => { triggerHaptic(20); setShowHints(!showHints); }}
                        className={`px-4 py-2 md:px-8 md:py-3 rounded-xl md:rounded-2xl font-black text-[10px] md:text-sm transition-all shadow-lg flex items-center gap-2 ${showHints ? 'bg-amber-500 text-white' : 'bg-white text-amber-600 border-2 border-amber-100'}`}
                    >
                        <span>{showHints ? '📖 SLĖPTI' : '💡 PAGALBA'}</span>
                    </button>
                </div>
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-6">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] px-2">Istorijos Dalys</h4>
            <div className="grid grid-cols-1 gap-3 md:gap-4">
                {availableParts.map((part) => (
                    <button
                        key={part.id}
                        onClick={() => addPart(part)}
                        disabled={!!feedback}
                        className="premium-button-3d group bg-white rounded-2xl md:rounded-[2.5rem] p-4 md:p-8 text-left border-slate-100 flex items-start gap-4 hover:scale-[1.02] active:scale-95 transition-all shadow-xl"
                    >
                        <div className="text-4xl md:text-6xl drop-shadow-lg">{part.emoji}</div>
                        <div className="flex-1">
                            <p className="text-sm md:text-xl text-slate-800 font-black uppercase leading-tight">{part.text}</p>
                            {showHints && (
                                <p className="text-[10px] md:text-xs text-amber-600 mt-2 font-bold italic">💡 {part.hint}</p>
                            )}
                        </div>
                        <div className="w-8 h-8 md:w-12 md:h-12 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 text-xl md:text-3xl group-hover:bg-indigo-50 group-hover:text-indigo-500 transition-colors">
                            +
                        </div>
                    </button>
                ))}
                {availableParts.length === 0 && (
                    <div className="p-12 text-center opacity-40 italic">Visos dalys pasirinktos! 🚀</div>
                )}
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-3 md:space-y-4">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] px-2">Tavo Istorija</h4>
            <div className="bg-slate-900 rounded-2xl md:rounded-[3rem] p-4 md:p-8 min-h-[150px] md:min-h-[250px] flex flex-col gap-3 md:gap-4 border-4 border-slate-800 shadow-inner overflow-y-auto max-h-[300px] md:max-h-none">
                {userSequence.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center opacity-30">
                        <div className="text-4xl md:text-6xl mb-4">📖</div>
                        <p className="text-xs md:text-sm font-bold text-slate-400">Pasirink įvykius iš viršaus...</p>
                    </div>
                ) : (
                    userSequence.map((part, index) => (
                        <button
                            key={part.id}
                            onClick={() => removePart(part)}
                            disabled={!!feedback}
                            className="bg-slate-800 rounded-xl md:rounded-2xl p-3 md:p-6 border-2 border-slate-700 text-left flex items-start gap-3 md:gap-4 animate-pop-in hover:border-rose-500/50"
                        >
                            <div className="flex flex-col items-center gap-1 shrink-0">
                                <div className="text-[8px] font-black text-indigo-400 uppercase tracking-tighter">{index + 1}</div>
                                <div className="text-3xl md:text-5xl">{part.emoji}</div>
                            </div>
                            <p className="text-xs md:text-lg text-white font-black uppercase leading-tight pt-1">{part.text}</p>
                        </button>
                    ))
                )}
            </div>

            {userSequence.length > 0 && (
                <div className="flex gap-2">
                    <button
                        onClick={clearSequence}
                        disabled={!!feedback}
                        className="flex-1 py-3 md:py-6 bg-slate-800 text-slate-400 rounded-xl md:rounded-2xl font-black text-xs md:text-xl border-b-4 border-slate-700 active:translate-y-1 active:border-b-0 transition-all disabled:opacity-20"
                    >
                        VALYTI 🔄
                    </button>
                    <button
                        onClick={checkAnswer}
                        disabled={userSequence.length !== gameData.story_parts.length || !!feedback}
                        className={`
                            flex-[2] py-3 md:py-6 rounded-xl md:rounded-2xl font-black text-xs md:text-xl transition-all uppercase tracking-widest border-b-8
                            ${userSequence.length === gameData.story_parts.length 
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-emerald-800 active:translate-y-1 active:border-b-0' 
                                : 'bg-slate-800 text-slate-600 border-slate-900 opacity-50'}
                        `}
                    >
                        BAIGTI ✓
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[3rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '📖' : '📜'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-3xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'IDEALU!' : 'KLAIDA!'}
                            </h2>
                            <div className={`p-4 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-lg font-bold text-slate-700 leading-relaxed text-center">
                                    {feedback === 'correct' ? 'Nuostabu! Sudėliojai visus įvykius teisinga tvarka!' : 'Pabandyk juos sukeisti vietomis, kad istorija turėtų prasmę!'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StoryScrambleGame;